import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  EmojiEvents,
  Lock as LockIcon,
} from '@mui/icons-material';
import gamificationService, { Badge } from '../../services/gamificationService';
import { format } from 'date-fns';

interface BadgesDisplayProps {
  studentId?: number;
}

const BadgesDisplay: React.FC<BadgesDisplayProps> = ({ studentId }) => {
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBadges();
  }, [studentId]);

  const loadBadges = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await gamificationService.getStudentBadges();
      setBadges(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const earnedBadges = badges.filter((b) => b.earned);
  const lockedBadges = badges.filter((b) => !b.earned);

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'LEGENDARY':
        return 'error';
      case 'EPIC':
        return 'secondary';
      case 'RARE':
        return 'primary';
      case 'COMMON':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <EmojiEvents sx={{ mr: 1, verticalAlign: 'middle' }} />
          Badges
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Earn badges by completing challenges and achieving milestones.
        </Typography>

        {earnedBadges.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Earned Badges ({earnedBadges.length})
            </Typography>
            <Grid container spacing={2}>
              {earnedBadges.map((badge) => (
                <Grid item xs={6} sm={4} md={3} key={badge.id}>
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="subtitle2">{badge.badgeName}</Typography>
                        {badge.description && (
                          <Typography variant="caption">{badge.description}</Typography>
                        )}
                        {badge.earnedAt && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Earned: {format(new Date(badge.earnedAt), 'MMM dd, yyyy')}
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        border: '2px solid',
                        borderColor: `${getRarityColor(badge.rarity)}.main`,
                        borderRadius: 2,
                        bgcolor: `${getRarityColor(badge.rarity)}.light`,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: `${getRarityColor(badge.rarity)}.main`,
                          mx: 'auto',
                          mb: 1,
                        }}
                      >
                        <EmojiEvents />
                      </Avatar>
                      <Typography variant="caption" fontWeight="bold">
                        {badge.badgeName}
                      </Typography>
                      <Chip
                        label={badge.rarity}
                        size="small"
                        color={getRarityColor(badge.rarity) as any}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {lockedBadges.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Locked Badges ({lockedBadges.length})
            </Typography>
            <Grid container spacing={2}>
              {lockedBadges.map((badge) => (
                <Grid item xs={6} sm={4} md={3} key={badge.id}>
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="subtitle2">{badge.badgeName}</Typography>
                        {badge.description && (
                          <Typography variant="caption">{badge.description}</Typography>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        opacity: 0.6,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'grey.400',
                          mx: 'auto',
                          mb: 1,
                        }}
                      >
                        <LockIcon />
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {badge.badgeName}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {badges.length === 0 && (
          <Alert severity="info">No badges available.</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BadgesDisplay;
